"use client";

import { Loader2 } from "lucide-react";
import { FormEvent, useRef, useState } from "react";

const inputClass =
  "w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[var(--color-primary)] focus:ring-2 focus:ring-[#b5e7e1] outline-none transition-all";
const labelClass = "block text-sm font-semibold text-gray-700 mb-2";

export default function ContactForm() {
  const formRef = useRef<HTMLFormElement>(null);
  const [status, setStatus] = useState<"idle" | "saving" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState("Something went wrong. Please try again.");

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setStatus("saving");
    setErrorMessage("Something went wrong. Please try again.");

    const formData = new FormData(event.currentTarget);

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(Object.fromEntries(formData.entries())),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        setErrorMessage(typeof data.error === "string" ? data.error : "Unable to send your message right now.");
        setStatus("error");
        return;
      }

      formRef.current?.reset();
      setStatus("success");
    } catch (error) {
      console.error("Contact form submit failed:", error);
      setStatus("error");
    }
  }

  return (
    <div className="bg-white p-8 rounded-[2rem] shadow-xl border border-gray-100">
      <h3 className="text-2xl font-bold text-[var(--color-secondary)] mb-6">Send a Message</h3>
      <form ref={formRef} onSubmit={handleSubmit} className="space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <label htmlFor="name" className={labelClass}>Your Name</label>
            <input
              type="text"
              id="name"
              name="name"
              required
              placeholder="Jane Doe"
              className={inputClass}
            />
          </div>
          <div>
            <label htmlFor="phone" className={labelClass}>Phone Number</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              placeholder="07123 456789"
              className={inputClass}
            />
          </div>
        </div>

        <div>
          <label htmlFor="email" className={labelClass}>Email Address</label>
          <input
            type="email"
            id="email"
            name="email"
            required
            placeholder="jane@example.com"
            className={inputClass}
          />
        </div>

        <div>
          <label htmlFor="childAge" className={labelClass}>Child's Age (Optional)</label>
          <input
            type="text"
            id="childAge"
            name="childAge"
            placeholder="e.g. 2 years old"
            className={inputClass}
          />
        </div>

        <div>
          <label htmlFor="message" className={labelClass}>Message</label>
          <textarea
            id="message"
            name="message"
            required
            rows={4}
            placeholder="Tell us about your childcare needs..."
            className={`${inputClass} resize-none`}
          />
        </div>

        <button
          type="submit"
          disabled={status === "saving"}
          className="flex w-full transform items-center justify-center gap-2 rounded-full bg-[var(--color-primary)] py-4 text-lg font-bold text-white shadow-lg transition-all duration-300 hover:-translate-y-1 hover:bg-[var(--color-primary-dark)] disabled:translate-y-0 disabled:opacity-70"
        >
          {status === "saving" && <Loader2 className="animate-spin" size={20} />}
          {status === "saving" ? "Sending..." : "Send Message"}
        </button>

        {status === "success" && (
          <p className="font-bold text-green-700">Thank you. Your message has been sent.</p>
        )}
        {status === "error" && (
          <p className="font-bold text-red-700">{errorMessage}</p>
        )}
      </form>
    </div>
  );
}
